import React, { useState } from 'react';
import classNames from 'classnames';
import Waypoint from 'react-waypoint';

import { Icon } from '@eb/eds-icon';
import { NewWindowChunky } from '@eb/eds-iconography';
import { Button } from '@eb/eds-button';
import { STYLE_FILL } from '@eb/eds-button';
import momentTz from 'moment-timezone';
import gettext from '@eb/gettext';
import isEmpty from 'lodash/isEmpty';

import { getEventPathWithTld } from '../constants';

const ICON_NEW_WINDOW = <NewWindowChunky />;

const EventLogo = ({ logo, thumbnails }) => {
    const altText = gettext('Event image');
    let cardImage;

    if (!thumbnails || isEmpty(thumbnails)) {
        if (!logo || !logo.url) return null;

        cardImage = <img src={logo.url} alt={altText} />;
    } else {
        const thumbnailSizes = thumbnails.map((image) => {
            const { width, url } = image;

            return `${url} ${width}w`;
        });

        cardImage = <img srcSet={thumbnailSizes} sizes="720px" alt={altText} />;
    }

    const logoClassName = classNames(['dc-event-card__image']);

    return <div className={logoClassName}>{cardImage}</div>;
};

const EventInfo = ({
    name,
    start,
    url,
    logo,
    thumbnails,
    locale,
    ebLive = false,
    fixed = false,
}) => {
    const formattedStartDatetime = () => {
        momentTz.locale(locale);
        const localTimezone = momentTz.tz.guess(true);
        const startMoment = momentTz.utc(start.utc);

        return startMoment.tz(localTimezone).format('LLLL (z)');
    };

    const headerContentClasses = classNames([
        'dc-header',
        { 'dc-header--fixed': fixed },
        'eds-l-pad-bottom-14',
        'eds-bg-color--background',
    ]);

    const hasImage =
        (!logo || !logo.url) && (!thumbnails || isEmpty(thumbnails));

    const eventCardClasses = classNames([
        'dc-event-card',
        { 'dc-event-card--no-logo': !hasImage },
        { 'eds-l-lg-pad-vert-8': !fixed },
        { 'eds-l-lw-pad-vert-8': !fixed },
        { 'eds-l-ln-pad-vert-8': !fixed },
        'eds-l-pad-vert-4',
    ]);

    const eventCardDateClasses = classNames(['eds-text-bm', 'eds-l-mar-bot-2']);

    const eventCardTitleClasses = classNames([
        'dc-event-card__title',
        'eds-text-hs',
        'eds-l-mar-bot-2',
    ]);

    const eventDetailsLinkClasses = classNames([
        { 'eds-show-down-mw': fixed },
        'eds-l-mar-bot-5',
        'eds-text-weight--heavy',
        'eds-text-color--control',
    ]);

    const joinEventButtonClasses = classNames([
        { 'eds-show-up-ln': ebLive && !fixed },
        { 'eds-hide': ebLive && fixed },
    ]);

    const handleJoinEventClick = (e) => {
        e.preventDefault();
        // This is not in production.  Update before launching EB Live publicly
    };

    return (
        <div className={headerContentClasses}>
            <EventLogo logo={logo} thumbnails={thumbnails} />
            <div className={eventCardClasses}>
                <div className="dc-event-card__content">
                    <div className="dc-event-card__details">
                        <p
                            className={eventCardDateClasses}
                            data-testid="event-date"
                        >
                            {formattedStartDatetime()}
                        </p>
                        <h1
                            className={eventCardTitleClasses}
                            data-testid="event-title"
                        >
                            {name.text}
                        </h1>
                        <a
                            className={eventDetailsLinkClasses}
                            data-testid="event-listing-url"
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {gettext('View event details')}
                            <span className="new-window-icon-wrapper eds-l-mar-left-2">
                                <Icon
                                    type={ICON_NEW_WINDOW}
                                    color="ui-blue"
                                    size="xsmall"
                                />
                            </span>
                        </a>
                    </div>
                    {ebLive && (
                        <div className="dc-event-card__buttons">
                            <Button
                                className={joinEventButtonClasses}
                                style={STYLE_FILL}
                                onClick={handleJoinEventClick}
                            >
                                {gettext('Join the event')}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const Header = ({ event, ebLive }) => {
    const [isHeaderOffscreen, setIsHeaderOffscreen] = useState(false);

    const handleHeaderLeave = (e) => {
        setIsHeaderOffscreen(true);
    };

    const handleHeaderEnter = (e) => {
        setIsHeaderOffscreen(false);
    };

    if (!event) {
        return;
    }

    const url = getEventPathWithTld(event.id);

    return (
        <>
            <Waypoint onEnter={handleHeaderEnter} onLeave={handleHeaderLeave}>
                <div>
                    <EventInfo
                        ebLive={ebLive}
                        locale={event.locale}
                        logo={event.logo}
                        thumbnails={event.logoThumbnails}
                        name={event.name}
                        start={event.start}
                        url={url}
                    />
                </div>
            </Waypoint>
            {isHeaderOffscreen && (
                <EventInfo
                    ebLive={ebLive}
                    fixed
                    locale={event.locale}
                    logo={event.logo}
                    thumbnails={event.logoThumbnails}
                    name={event.name}
                    start={event.start}
                    url={url}
                />
            )}
        </>
    );
};

export default Header;
